import { useState } from 'preact/hooks';
import { CardGrid, Empty, ErrorBox, Loading } from '../components/ui';
import { api, type CardSummary } from '../lib/api';
import { Link, navigate } from '../lib/router';
import { errorText, toast, useAsync, useIsAdmin } from '../lib/store';

interface CollectionRow {
  id: string;
  name: string;
  description: string | null;
  count: number;
}

export function Collections() {
  const { data, error, loading, reload } = useAsync(() => api.get<{ items: CollectionRow[] }>('/collections'), []);
  const [name, setName] = useState('');
  const admin = useIsAdmin();
  const create = async (e: Event) => {
    e.preventDefault();
    try {
      const c = await api.post<{ id: string }>('/collections', { name });
      setName('');
      navigate(`/collections/${c.id}`);
    } catch (err) {
      toast(errorText(err), 'error');
      reload();
    }
  };
  return (
    <>
      <h1>Collections</h1>
      {admin && (
      <form class="row" onSubmit={create} style={{ marginBottom: 'var(--bk-space-6)' }}>
        <input class="dr-input grow" aria-label="New collection name" placeholder="Christmas 2026, Weeknight…" value={name} onInput={(e) => setName(e.currentTarget.value)} required />
        <button class="dr-btn dr-btn--primary">New collection</button>
      </form>
      )}
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorBox error={error} />
      ) : !data!.items.length ? (
        <Empty>{admin ? 'No collections yet. Use “Collect” on any card.' : 'No collections yet.'}</Empty>
      ) : (
        <div class="grid">
          {data!.items.map((c) => (
            <Link class="card dr-frame dr-frame--tight" href={`/collections/${c.id}`}>
              <div class="card-body">
                <div class="card-title">{c.name}</div>
                {c.description && <div class="card-summary">{c.description}</div>}
                <div class="card-face dr-plate">
                  {c.count} card{c.count === 1 ? '' : 's'}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {admin && (
        <p style={{ marginTop: 'var(--bk-space-8)' }}>
          <Link href="/trash">Trash →</Link>
        </p>
      )}
    </>
  );
}

export function Collection({ id }: { id: string }) {
  const { data, error, loading, reload } = useAsync(() => api.get<{ id: string; name: string; description: string | null; cards: CardSummary[] }>(`/collections/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const admin = useIsAdmin();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const c = data!;

  const save = async (patch: Record<string, unknown>) => {
    try {
      await api.patch(`/collections/${id}`, patch);
      reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const move = (i: number, d: number) => {
    const ids = c.cards.map((x) => x.id);
    const [x] = ids.splice(i, 1);
    ids.splice(i + d, 0, x);
    void save({ card_ids: ids });
  };

  return (
    <>
      <p>
        <Link href="/collections">← Collections</Link>
      </p>
      {editing ? (
        <form
          class="panel stack"
          onSubmit={(e) => {
            e.preventDefault();
            void save({ name, description: desc || null }).then(() => setEditing(false));
          }}
        >
          <input class="dr-input" aria-label="Name" value={name} onInput={(e) => setName(e.currentTarget.value)} required />
          <input class="dr-input" aria-label="Description" placeholder="Description" value={desc} onInput={(e) => setDesc(e.currentTarget.value)} />
          <div class="row">
            <button class="dr-btn dr-btn--primary">Save</button>
            <button type="button" class="dr-btn bk-ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div class="row-between">
          <div>
            <h1>{c.name}</h1>
            {c.description && <p class="muted">{c.description}</p>}
          </div>
          {admin && (
          <div class="row">
            <button
              class="dr-btn"
              onClick={() => {
                setName(c.name);
                setDesc(c.description ?? '');
                setEditing(true);
              }}
            >
              Rename
            </button>
            <button
              class="dr-btn bk-danger"
              onClick={async () => {
                if (!confirm(`Delete the collection “${c.name}”? The cards themselves are kept.`)) return;
                await api.del(`/collections/${id}`);
                navigate('/collections');
              }}
            >
              Delete
            </button>
          </div>
          )}
        </div>
      )}
      {!c.cards.length ? (
        <Empty>Empty. Open a card and choose “Collect”.</Empty>
      ) : (
        <>
          <CardGrid items={c.cards} />
          {admin && (
          <details style={{ marginTop: 'var(--bk-space-6)' }}>
            <summary>Reorder or remove</summary>
            <ul class="rev-list">
              {c.cards.map((x, i) => (
                <li class="row-between" style={{ padding: 'var(--bk-space-2) 0' }}>
                  <span>{x.title}</span>
                  <span class="row">
                    <button class="dr-btn bk-ghost bk-sm" aria-label={`Move ${x.title} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                      ↑
                    </button>
                    <button class="dr-btn bk-ghost bk-sm" aria-label={`Move ${x.title} down`} disabled={i === c.cards.length - 1} onClick={() => move(i, 1)}>
                      ↓
                    </button>
                    <button
                      class="dr-btn bk-ghost bk-sm"
                      aria-label={`Remove ${x.title}`}
                      onClick={async () => {
                        await api.del(`/collections/${id}/cards/${x.id}`);
                        reload();
                      }}
                    >
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </details>
          )}
        </>
      )}
    </>
  );
}
